/**
 * The entrance choreography, in one place.
 *
 * Ordered explicitly rather than emerging from a scatter of CSS `animation-delay`s
 * — the order *is* the design, so it should be readable in one function: figures
 * count up, the pipeline draws itself stage by stage, product cards settle, the
 * fit rings sweep from empty to their value, then the evidence strip.
 *
 * Driven from an effect rather than pure CSS because two of the four steps cannot
 * be expressed as a keyframe: counting a number up needs to interpolate and format
 * it, and sweeping a ring needs the target `stroke-dashoffset` read off the DOM.
 *
 * Every step is a no-op under `prefers-reduced-motion` — values land immediately at
 * their final state, nothing is left hidden, and no timer runs.
 */

/** True when the viewer has asked for less motion. */
export function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Land `.is-in` on each node, `step` ms apart. */
function settle(nodes: Iterable<Element>, step: number, from: number, timers: number[]): void {
  [...nodes].forEach((node, index) => {
    timers.push(
      window.setTimeout(() => {
        node.classList.add('is-in');
      }, from + index * step),
    );
  });
}

/**
 * Count one figure up to its `data-to`, formatting as it goes.
 *
 * `data-kind` picks the format so the interpolation is never shown raw: a
 * percentage must not flash `0.4413` on the way to `93.0%`.
 */
function countUp(el: HTMLElement, ms: number): void {
  const to = Number.parseFloat(el.dataset.to ?? '0');
  const kind = el.dataset.kind ?? 'int';
  const show = (value: number): void => {
    el.textContent =
      kind === 'pct'
        ? `${(value * 100).toFixed(1)}%`
        : kind === 'conf'
          ? value.toFixed(2)
          : Math.round(value).toLocaleString('en-GB');
  };
  if (reducedMotion()) {
    show(to);
    return;
  }
  const start = performance.now();
  const tick = (now: number): void => {
    // Clamped at BOTH ends. The first rAF callback can carry a timestamp from
    // before the frame we scheduled in, so `now - start` goes negative — and with
    // an ease-out cubic a negative progress produces a negative OUTPUT, which
    // flashed figures like "-1,134" and "-0.19" on screen for a frame or two.
    const p = Math.max(0, Math.min(1, (now - start) / ms));
    // ease-out cubic: quick off the mark, gentle landing, so the final value is
    // legible rather than still racing when the eye arrives.
    show(to * (1 - Math.pow(1 - p, 3)));
    if (p < 1) requestAnimationFrame(tick);
    else show(to);
  };
  show(0);
  requestAnimationFrame(tick);
}

/** Draw a pipeline left to right by re-triggering its per-stage transitions. */
function drawSpine(spine: Element, from: number, step: number, timers: number[]): void {
  const stages = [...spine.querySelectorAll<HTMLElement>('.ds-sl__stg')];
  stages.forEach((stage, index) => {
    stage.style.setProperty('--d', `${String(from + index * step)}ms`);
  });
  // Strip the lit class, then restore it next frame — otherwise the stages are
  // *already* in their final state and the transition has nothing to run from.
  stages.forEach((stage) => {
    stage.classList.remove('is-lit');
  });
  const light = (): void => {
    stages.forEach((stage) => {
      stage.classList.add('is-lit');
    });
  };
  // requestAnimationFrame is throttled or paused in a background tab, so it is not
  // a guarantee. Without this fallback, opening the page in a background tab and
  // switching to it later could leave the spine grey and — because the product name
  // under each node is transparent until lit — leave those names invisible. The
  // timer restores them regardless; `light()` is idempotent, so both paths firing
  // is harmless.
  timers.push(window.setTimeout(light, 120));
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      light();
      // Clear the delays afterwards, so a later theme switch or hover does not
      // inherit a staggered pause.
      timers.push(
        window.setTimeout(
          () => {
            stages.forEach((stage) => {
              stage.style.removeProperty('--d');
            });
          },
          from + stages.length * step + 900,
        ),
      );
    });
  });
}

/**
 * Sweep each fit ring from empty to its value.
 *
 * The ring is *rendered* at its final offset, so it reads correctly with no script
 * at all — which means simply assigning that same value here changes nothing and no
 * transition ever runs. That was the bug: the inline style was set, the audit saw it
 * set, and the ring had been sitting at its final value the whole time.
 *
 * So: reset to empty with the transition suppressed, force the reset to commit, then
 * restore the transition and assign the target. Same remove-then-restore shape the
 * pipeline lighting uses, for the same reason.
 */
function sweep(rings: Iterable<SVGCircleElement>, step: number, from: number, timers: number[]): void {
  [...rings].forEach((ring, index) => {
    const target = ring.dataset.off ?? '0';
    // The dash array is the full circumference, i.e. an empty ring.
    const empty = ring.getAttribute('stroke-dasharray') ?? target;
    timers.push(
      window.setTimeout(
        () => {
          ring.style.transition = 'none';
          ring.style.strokeDashoffset = empty;
          // Reading layout flushes the two declarations above, so the reset lands
          // as a jump rather than being coalesced into the sweep that follows.
          void ring.getBoundingClientRect();
          ring.style.transition = '';
          ring.style.strokeDashoffset = target;
        },
        from + index * step,
      ),
    );
  });
}

/**
 * Run the sequence over one panel — the hero, or a row just opened.
 *
 * `step` is tighter for a row than for the hero on purpose. The hero is the page
 * arriving and can afford to be deliberate; a row is a response to a click, where
 * anything past about a second and a half stops reading as motion and starts
 * reading as lag. Same choreography, different tempo.
 *
 * Returns a cleanup that cancels every pending timer, so unmounting mid-sequence
 * cannot touch a detached node.
 */
export function revealPanel(panel: Element | null, opts: { from?: number; step?: number } = {}): () => void {
  const timers: number[] = [];
  if (panel === null) return () => undefined;
  const from = opts.from ?? 0;
  const step = opts.step ?? 90;

  const figures = panel.querySelectorAll<HTMLElement>('[data-to]');
  const rings = panel.querySelectorAll<SVGCircleElement>('.ds-sl__ring circle.is-val');
  const spine = panel.querySelector('.ds-sl__spine');
  const cards = panel.querySelectorAll('.ds-sl__card');
  const tail = panel.querySelectorAll('.ds-sl__sep, .ds-sl__ev');

  if (reducedMotion()) {
    figures.forEach((figure) => {
      countUp(figure, 0);
    });
    rings.forEach((ring) => {
      ring.style.strokeDashoffset = ring.dataset.off ?? '0';
    });
    [...cards, ...tail].forEach((node) => {
      node.classList.add('is-in');
    });
    spine?.querySelectorAll('.ds-sl__stg').forEach((stage) => {
      stage.classList.add('is-lit');
    });
    return () => undefined;
  }

  figures.forEach((figure, index) => {
    timers.push(
      window.setTimeout(
        () => {
          countUp(figure, 720);
        },
        from + index * 90,
      ),
    );
  });

  const spineAt = from + 220;
  if (spine !== null) drawSpine(spine, spineAt, step, timers);
  const stageCount = spine?.querySelectorAll('.ds-sl__stg').length ?? 0;
  const afterSpine = spineAt + stageCount * step;

  settle(cards, step - 10, afterSpine, timers);
  sweep(rings, step - 10, afterSpine + 120, timers);
  settle(tail, step, afterSpine + 220, timers);

  return () => {
    timers.forEach((timer) => {
      window.clearTimeout(timer);
    });
  };
}

/**
 * Bring elements in as they arrive on screen.
 *
 * **JS opts them into being hidden, not the markup.** `is-pending` is applied here
 * rather than shipped in the class list, so a script that never runs leaves every
 * row plainly visible. Getting this backwards is how a scroll reveal turns eleven
 * rows into eleven invisible ones.
 *
 * A safety timer reveals everything after a couple of seconds regardless of the
 * observer — a layout the observer never reports (a zero-height container, a
 * throttled background tab) must not cost the reader the list.
 *
 * Returns a cleanup that disconnects the observer and cancels the timer.
 */
export function revealOnEnter(
  nodes: Iterable<Element>,
  opts: { stagger?: number; immediate?: boolean } = {},
): () => void {
  const items = [...nodes];
  if (items.length === 0) return () => undefined;
  const stagger = opts.stagger ?? 60;

  if (reducedMotion()) {
    items.forEach((n) => {
      n.classList.add('is-in');
    });
    return () => undefined;
  }

  items.forEach((n) => {
    n.classList.add('is-pending');
  });

  const timers: number[] = [];
  const show = (node: Element, index: number): void => {
    timers.push(
      window.setTimeout(() => {
        node.classList.add('is-in');
      }, index * stagger),
    );
  };

  const cancel = (): void => {
    timers.forEach((t) => {
      window.clearTimeout(t);
    });
  };

  // Above the fold, or nowhere to observe from: reveal at once. The feature check
  // is not test scaffolding — an environment without IntersectionObserver would
  // otherwise throw here and take the whole screen down over an animation, which
  // is a spectacularly bad trade.
  if (opts.immediate === true || typeof IntersectionObserver === 'undefined') {
    items.forEach(show);
    return cancel;
  }

  let seen = 0;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target, seen);
        seen += 1;
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -6% 0px' },
  );
  items.forEach((n) => {
    io.observe(n);
  });

  const safety = window.setTimeout(() => {
    items.forEach((n) => {
      n.classList.add('is-in');
    });
    io.disconnect();
  }, 2200);

  return () => {
    io.disconnect();
    window.clearTimeout(safety);
    cancel();
  };
}
